import React from "react";
import {
  ArrowRight,
  Building,
  Building2,
  LandPlot,
  Layers,
  MapPin,
} from "lucide-react";

import { featuredProjects, propertyTypes } from "../mock";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

const iconMap = {
  apartment: Building,
  floors: Layers,
  plots: LandPlot,
  builder: Building2,
};

const Pattern = ({ tone = "teal" }) => {
  const styles =
    tone === "gold"
      ? "from-[#D4A72C]/18 via-white/0 to-[#D4A72C]/10"
      : "from-[#0F766E]/18 via-white/0 to-[#0F766E]/10";

  const blob =
    tone === "gold"
      ? "bg-[radial-gradient(circle,rgba(212,167,44,0.22),transparent_62%)]"
      : "bg-[radial-gradient(circle,rgba(15,118,110,0.22),transparent_62%)]";

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
      <div className={`absolute inset-0 bg-gradient-to-br ${styles}`} />
      <div className="absolute inset-0 opacity-[0.16] [background-image:linear-gradient(to_right,rgba(15,23,42,0.12)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,0.12)_1px,transparent_1px)] [background-size:28px_28px]" />
      <div className={`absolute -right-16 -top-16 h-48 w-48 rounded-full ${blob}`} />
      <div className={`absolute -left-20 -bottom-24 h-56 w-56 rounded-full ${blob} opacity-60`} />
    </div>
  );
};

export const PropertyTypesSection = () => {
  return (
    <section id="property" className="scroll-mt-28">
      <div className="mx-auto w-full max-w-6xl px-5 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_0.9fr] lg:items-end">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/55 px-3 py-1 text-xs font-medium text-slate-700 shadow-sm backdrop-blur-2xl">
              <MapPin className="h-3.5 w-3.5 text-[#0F766E]" />
              Property styles we deal in
            </div>
            <h2 className="mt-4 text-pretty text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
              Pick the vibe. We’ll find the right fit.
            </h2>
            <p className="mt-3 max-w-xl text-pretty text-[15px] leading-7 text-slate-600 sm:text-base">
              Just like a premium brand homepage sets the mood instantly, this section helps visitors
              understand the kind of residential options Vrinda Homes works with.
            </p>
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white/60 p-5 shadow-sm backdrop-blur-2xl">
            <div className="pointer-events-none absolute inset-0">
              <img
                src={featuredProjects?.[0]?.imageUrl}
                alt="Shortlist backdrop"
                className="h-full w-full object-cover opacity-[0.18]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-white/85 via-white/78 to-white/90" />
              <div className="absolute inset-0 [background:radial-gradient(520px_220px_at_20%_20%,rgba(15,118,110,0.16),transparent_60%),radial-gradient(520px_220px_at_85%_30%,rgba(212,167,44,0.16),transparent_60%)]" />
            </div>

            <div className="relative">
              <div className="text-sm font-semibold text-slate-950">
                Tell us your requirement in 30 seconds
              </div>
              <div className="mt-1 text-sm text-slate-600">
                Sector preference + budget + timeline = a sharper shortlist.
              </div>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <a href="#contact" className="flex-1">
                  <Button className="w-full bg-[#0B1220] text-white hover:bg-[#121b31]">
                    Request a shortlist
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <a href="#services" className="flex-1">
                  <Button
                    variant="outline"
                    className="w-full border-slate-300 bg-white/70 backdrop-blur-2xl hover:bg-white"
                  >
                    View services
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {propertyTypes.map((p, idx) => {
            const Icon = iconMap[p.iconKey] || Building;
            const tone = idx % 2 === 0 ? "teal" : "gold";

            return (
              <Card
                key={p.id}
                className="group relative overflow-hidden border-slate-200/70 bg-white/65 shadow-sm backdrop-blur-2xl transition-transform hover:-translate-y-0.5"
              >
                {/* Thumbnail layer */}
                <div className="absolute inset-0 -z-0">
                  {p.thumbUrl ? (
                    <img
                      src={p.thumbUrl}
                      alt={p.title}
                      className="h-full w-full object-cover opacity-[0.12] transition-opacity duration-300 group-hover:opacity-[0.22]"
                      loading="lazy"
                    />
                  ) : null}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/50 to-white/78" />
                </div>

                <Pattern tone={tone} />

                <CardHeader className="relative">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-base text-slate-950">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/70 shadow-sm backdrop-blur-2xl">
                          <Icon
                            className={
                              tone === "gold"
                                ? "h-5 w-5 text-[#D4A72C]"
                                : "h-5 w-5 text-[#0F766E]"
                            }
                          />
                        </span>
                        {p.title}
                      </CardTitle>
                      <div className="mt-2 text-sm leading-6 text-slate-600">
                        {p.subtitle}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {p.tags.map((t) => (
                      <Badge
                        key={t}
                        variant="secondary"
                        className="bg-slate-900/5 text-slate-700"
                      >
                        {t}
                      </Badge>
                    ))}
                  </div>
                </CardHeader>

                <CardContent className="relative">
                  <div className="space-y-2 text-sm text-slate-700">
                    {p.points.map((x) => (
                      <div key={x} className="flex items-start gap-2">
                        <span
                          className={
                            tone === "gold"
                              ? "mt-0.5 h-1.5 w-1.5 rounded-full bg-[#D4A72C]"
                              : "mt-0.5 h-1.5 w-1.5 rounded-full bg-[#0F766E]"
                          }
                        />
                        <div className="leading-6">{x}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5">
                    <a href="#contact" className="inline-flex">
                      <Button
                        variant="outline"
                        className="border-slate-300 bg-white/70 backdrop-blur-2xl transition-colors hover:bg-white"
                      >
                        Enquire
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
